import { Alert, Button, Card, Col, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import Status from "../../../enums/Status";
import IInvite from "../../../models/invite/IInvite";
import { IInviteValidation } from "./Validation";
import { useTranslation } from "react-i18next";
import FloatingTextField from "../../../components/formFields/floatingText/FloatingTextField";
import LargeFloatingTextField from "../../../components/formFields/largeFloatingText/LargeFloatingTextField";
import IInvitee from "../../../models/invite/IInvitee";
import { Icon } from "../../../components/icon/Icon";
import DropdownSelect from "../../../components/formFields/dropdownSelect/DropdownSelect";
import IInviteLength from "../../../models/invite/IInviteLength";

interface INewUserInviteFormProps {
    invite: IInvite;
    handleReasonChange: (value: string) => void;
    handleInviteeChange: <K extends keyof IInvitee>(index: number, key: K, value: IInvitee[K]) => void;
    handleInviteeAdded: () => void;
    handleInviteeRemoved: (index: number) => void;
    handleSubmit: () => void;
    status: Status;
    validation: IInviteValidation;
}

export const NewUserInviteForm = (props: INewUserInviteFormProps) => {
    const { t } = useTranslation();

    const isReadOnly = [Status.SAVE_SUCCESS, Status.SAVING].includes(props.status);

    const inviteLengths: IInviteLength[] = [
        { days: 30, label: t("it.invite.form.fields.duration.valueLabel.days", { days: 30 }) },
        { days: 90, label: t("it.invite.form.fields.duration.valueLabel.days", { days: 90 }) },
        { days: -1, label: t("it.invite.form.fields.duration.valueLabel.indefinite") }
    ];

    return (
        <Card.Text as="div">
            <Row>
                <Col>
                    <Row className="mb-3">
                        <Col>
                            <h3>{t("it.invite.form.title")}</h3>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <LargeFloatingTextField required
                                type="text"
                                height={100}
                                label={t("it.invite.form.fields.reason.label")}
                                placeholder={t("it.invite.form.fields.reason.placeholder")}
                                value={props.invite.reason}
                                disabled={isReadOnly}
                                onChange={value => props.handleReasonChange(value)}
                                validation={props.validation.reason}
                                validationMessage={t("it.invite.form.fields.reason.validation")}
                                />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <p className="mb-1">
                                {t("it.invite.form.fields.invitees.title")}
                            </p>

                            <p className="mb-3 text-muted small">
                                {t("it.invite.form.fields.invitees.description")}
                            </p>
                        </Col>
                    </Row>

                    {
                        props.invite.invitees.map((invitee, index) => {
                            const durationLabel = inviteLengths.find(l => l.days === invitee.duration)?.label || t("general.pleaseSelect");

                            return (
                                <Row key={index} className="mb-3 border-bottom">
                                    <Col>
                                        <Row>
                                            <Col>
                                                <p className="mb-2">
                                                    {t("it.invite.form.fields.name.title")}
                                                </p>
                                            </Col>

                                            {
                                                props.invite.invitees.length > 1 &&
                                                <Col xs="auto">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("general.remove")}</Tooltip>}>
                                                        <Button variant="outline-danger mb-1" size="sm" onClick={() => props.handleInviteeRemoved(index)} disabled={isReadOnly}>
                                                            <Icon iconName="Trash" />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </Col>
                                            }
                                        </Row>

                                        <Row>
                                            <Col lg={6}>
                                            
                                                <FloatingTextField required
                                                    label={t("it.invite.form.fields.name.firstName.label")}
                                                    type="text"
                                                    placeholder={t("it.invite.form.fields.name.firstName.placeholder")}
                                                    value={invitee.firstName}
                                                    disabled={isReadOnly}
                                                    onChange={value => props.handleInviteeChange(index, "firstName", value)}
                                                    validation={props.validation.invitees[index]?.firstName}
                                                    validationMessage={t("it.invite.form.fields.name.firstName.validation")}
                                                    />

                                            </Col>
                                            <Col lg={6}>
                                            
                                                <FloatingTextField required
                                                    label={t("it.invite.form.fields.name.lastName.label")}
                                                    type="text"
                                                    placeholder={t("it.invite.form.fields.name.lastName.placeholder")}
                                                    value={invitee.lastName}
                                                    disabled={isReadOnly}
                                                    onChange={value => props.handleInviteeChange(index, "lastName", value)}
                                                    validation={props.validation.invitees[index]?.lastName}
                                                    validationMessage={t("it.invite.form.fields.name.lastName.validation")}
                                                    />

                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col xs={12} md={8}>
                                                <FloatingTextField required
                                                    label={t("it.invite.form.fields.emailAddress.label")}
                                                    type="email"
                                                    placeholder={t("it.invite.form.fields.emailAddress.placeholder")}
                                                    value={invitee.emailAddress}
                                                    disabled={isReadOnly}
                                                    onChange={value => props.handleInviteeChange(index, "emailAddress", value)}
                                                    validation={props.validation.invitees[index]?.emailAddress}
                                                    validationMessage={t("it.invite.form.fields.emailAddress.validation")}
                                                    />
                                            </Col>

                                            <Col xs={12} md={4}>
                                                <DropdownSelect required
                                                    title={t("it.invite.form.fields.duration.title")}
                                                    items={inviteLengths}
                                                    valueLabel={durationLabel}
                                                    itemValueField="days"
                                                    itemLabelField="label"
                                                    disabled={isReadOnly}
                                                    value={invitee.duration.toString()}
                                                    onchange={value => props.handleInviteeChange(index, "duration", value.days)}
                                                    validation={props.validation.invitees[index]?.duration}
                                                    validationMessage={t("it.invite.form.fields.duration.validation")}
                                                    />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            );
                        })
                    }

                    <Row className="mb-3">
                        <Col>
                            <Button variant="outline-primary" onClick={props.handleInviteeAdded} disabled={isReadOnly}>
                                <Icon iconName="Plus" endMargin={1} />
                                {t("general.add")}
                            </Button>
                        </Col>
                    </Row>

                    <Row>
                        <Col>

                            <p className="mb-3 fw-bold small text-danger">
                                {t("it.invite.form.securityWarning")}
                            </p>

                            {
                                props.status === Status.SAVE_ERROR &&
                                <Alert variant="danger">
                                    {t("it.invite.errors.saveInvite")}
                                </Alert>
                            }

                            {
                                props.status === Status.SAVE_SUCCESS &&
                                <Alert variant="success">
                                    {t("it.invite.success")}
                                </Alert>
                            }

                            <div className="d-block">
                                <Button variant="primary" size="lg" className="w-100" onClick={props.handleSubmit} disabled={isReadOnly}>
                                    {
                                        props.status === Status.SAVING &&
                                        <Spinner size="sm" className="me-2" />
                                    }
                                    {
                                        props.status !== Status.SAVING
                                            ? t("general.submit")
                                            : t("it.invite.submitting")
                                    }
                                </Button>
                            </div>

                        </Col>
                    </Row>
                </Col>
            </Row>
        </Card.Text>
    );
}